'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Package, Calendar, ArrowRight, ArrowDown, ArrowUp, RefreshCw, Trash2 } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { stockEntries, workOrders } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import type { StockEntry } from '../types';

// 입출고 등록 모달
function StockEntryAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [items, setItems] = useState([{ itemName: '', qty: 1, uom: 'EA', rate: 0 }]);

  const addItem = () => setItems([...items, { itemName: '', qty: 1, uom: 'EA', rate: 0 }]);
  const removeItem = (index: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== index)); };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="입출고 등록"
      description="생산 관련 재고 입출고를 등록합니다."
      size="xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="전표번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="입출고 유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="manufacture">생산 입고</option>
              <option value="material_issue">원자재 출고</option>
              <option value="material_transfer">재고 이동</option>
            </FormSelect>
          </FormField>
          <FormField label="작업지시 참조">
            <FormSelect>
              <option value="">선택 안함</option>
              {workOrders.filter(wo => wo.status === 'in_progress').map((wo) => (
                <option key={wo.id} value={wo.id}>{wo.code} - {wo.itemName}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="전기일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="출고 창고">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="WH001">원자재 창고</option>
              <option value="WH002">부품 창고</option>
              <option value="WH003">생산 창고</option>
            </FormSelect>
          </FormField>
          <FormField label="입고 창고">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="WH001">완제품 창고</option>
              <option value="WH002">생산 창고</option>
            </FormSelect>
          </FormField>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">품목 목록</h3>
            <Button variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-4 h-4 mr-1" />품목 추가
            </Button>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">품목</th>
                  <th className="text-right p-3 font-medium w-20">수량</th>
                  <th className="text-center p-3 font-medium w-20">단위</th>
                  <th className="text-right p-3 font-medium w-28">단가</th>
                  <th className="text-right p-3 font-medium w-28">금액</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((_, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-2">
                      <FormSelect>
                        <option value="">품목 선택</option>
                        <option value="mat-001">원지 (A등급)</option>
                        <option value="mat-002">골심지</option>
                        <option value="mat-003">접착제</option>
                        <option value="itm-001">택배 박스 (소)</option>
                        <option value="itm-002">택배 박스 (중)</option>
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={1} defaultValue={1} className="text-right" /></td>
                    <td className="p-2">
                      <FormSelect>
                        <option value="EA">EA</option>
                        <option value="M">M</option>
                        <option value="KG">KG</option>
                      </FormSelect>
                    </td>
                    <td className="p-2"><FormInput type="number" min={0} defaultValue={0} className="text-right" /></td>
                    <td className="p-2 text-right font-medium">₩0</td>
                    <td className="p-2">
                      <button onClick={() => removeItem(index)} className="p-1 rounded hover:bg-accent" disabled={items.length === 1}>
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <FormField label="비고">
          <FormTextarea rows={2} placeholder="입출고 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function StockEntryListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return stockEntries.filter((entry) => {
      const matchesSearch =
        entry.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (entry.workOrderCode && entry.workOrderCode.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType = selectedType === 'all' || entry.entryType === selectedType;

      return matchesSearch && matchesType;
    });
  }, [searchQuery, selectedType]);

  const entryTypeConfig: Record<string, { label: string; icon: typeof ArrowDown; color: string }> = {
    manufacture: { label: '생산 입고', icon: ArrowDown, color: 'green' },
    material_transfer: { label: '재고 이동', icon: RefreshCw, color: 'blue' },
    material_issue: { label: '원자재 출고', icon: ArrowUp, color: 'orange' },
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    };
    return colors[type] || colors.blue;
  };

  // 유형별 통계
  const typeStats = useMemo(() => {
    const stats: Record<string, { count: number; amount: number }> = {};
    stockEntries.forEach((entry) => {
      if (!stats[entry.entryType]) {
        stats[entry.entryType] = { count: 0, amount: 0 };
      }
      stats[entry.entryType].count += 1;
      stats[entry.entryType].amount += entry.totalAmount;
    });
    return stats;
  }, []);

  const columns = [
    {
      key: 'code',
      label: '전표번호',
      width: '14%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'entryType',
      label: '유형',
      width: '14%',
      render: (value: unknown) => {
        const type = value as string;
        const config = entryTypeConfig[type];
        const Icon = config.icon;
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <div className={`p-1 rounded flex-shrink-0 ${getTypeColor(config.color)}`}>
              <Icon className="w-3 h-3" />
            </div>
            <span className="text-sm">{config.label}</span>
          </div>
        );
      },
    },
    {
      key: 'workOrderCode',
      label: '작업지시',
      width: '14%',
      render: (value: unknown) =>
        value ? (
          <span className="font-mono text-sm text-muted-foreground whitespace-nowrap">{String(value)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'postingDate',
      label: '전기일',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <div className="flex items-center gap-1 text-sm whitespace-nowrap">
          <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span>{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'totalQty',
      label: '수량',
      width: '10%',
      align: 'right' as const,
      render: (value: unknown) => (
        <span className="font-medium whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'totalAmount',
      label: '금액',
      width: '16%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <button className="p-1.5 rounded hover:bg-accent" title="보기">
          <Eye className="w-4 h-4 text-muted-foreground" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="재고 입출고"
        description="생산 관련 재고 입출고 내역을 조회합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            입출고 등록
          </button>
        }
      />

      {/* 유형별 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(entryTypeConfig).map(([type, config]) => {
          const Icon = config.icon;
          const stat = typeStats[type] || { count: 0, amount: 0 };
          return (
            <button
              key={type}
              onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
              className={`p-4 rounded-lg border text-left transition-colors ${
                selectedType === type
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:bg-accent/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getTypeColor(config.color)}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{config.label}</div>
                  <div className="text-xl font-bold">{stat.count}건</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(stat.amount)}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="전표번호, 작업지시번호 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 유형 필터 */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 유형</option>
          {Object.entries(entryTypeConfig).map(([type, config]) => (
            <option key={type} value={type}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 선택된 항목 상세 (첫 번째 항목 표시) */}
      {filteredData.length > 0 && (
        <div className="p-4 rounded-lg bg-card border border-border">
          <h3 className="font-semibold mb-4">최근 입출고 상세</h3>
          <div className="space-y-3">
            {filteredData[0].items.map((item) => (
              <div
                key={item.id}
                className="p-3 rounded-lg bg-accent/50 border border-border"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <div className="font-medium">{item.itemName}</div>
                      <div className="text-xs text-muted-foreground">{item.itemCode}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatCurrency(item.amount)}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.qty} {item.uom} × {formatCurrency(item.rate)}
                    </div>
                  </div>
                </div>
                {(item.sourceWarehouse || item.targetWarehouse) && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    {item.sourceWarehouse && <span>{item.sourceWarehouse}</span>}
                    {item.sourceWarehouse && item.targetWarehouse && (
                      <ArrowRight className="w-4 h-4" />
                    )}
                    {item.targetWarehouse && <span>{item.targetWarehouse}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 입출고 등록 모달 */}
      <StockEntryAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
